import apis from "../../utils/apis";
import { getUserID, getUserType } from "./ChatAction";
import { showFeedbackModal } from "./UserFeedbackActions";
import { ThunkDispatch } from "redux-thunk";
import { RootState } from "..";
import type { AnyAction, Dispatch } from "redux";
import { BotAction, ChatMessage } from "../reducer/HelpReducer";
import {
  CREATE_TICKET_REQUEST, CREATE_TICKET_SUCCESS, FETCH_ALL_TICKETS_REQUEST, FETCH_ALL_TICKETS_SUCCESS, FETCH_ALL_USER_TICKETS_REQUEST, FETCH_ALL_USER_TICKETS_SUCCESS, FETCH_CATEGORIES_REQUEST, FETCH_CATEGORIES_SUCCESS,
  FETCH_HELP_REQUEST, FETCH_HELP_SUCCESS, FETCH_RESOLVED_TICKETS_REQUEST, FETCH_RESOLVED_TICKETS_SUCCESS, FETCH_TICKET_REQUEST, FETCH_TICKET_SUCCESS, FETCH_UNRESOLVED_TICKETS_REQUEST, FETCH_UNRESOLVED_TICKETS_SUCCESS,
  FETCH_USER_HELP_REQUEST, FETCH_USER_HELP_SUCCESS, RECEIVE_MESSAGE, RESOLVE_TICKET_REQUEST, RESOLVE_TICKET_SUCCESS, SEND_MESSAGE_REQUEST, SEND_MESSAGE_SUCCESS
} from "../types";
import { DropdownOption } from "../../components/shared/TailwindComponents/Dropdown";
import { handleApiError } from "./utils/utils";
import { translate } from '../../i18n/translate';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';



export const sendMessage = (userInput: string) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  dispatch({ type: SEND_MESSAGE_REQUEST, payload: userInput });

  const formData = new FormData();
  formData.append('UserInput', userInput);
  formData.append('UserID', getUserID());
  formData.append('UserType', getUserType());

  try {
    const response = await apis({
      method: 'POST',
      url: 'support/chat',
      data: formData,
    });
    if (response && response.status === 200) {
      const chatResponse = response.data.ChatResponse;
      if (typeof chatResponse !== 'string') {
        return;
      }


      const regex = /```json(?:\s*)([\s\S]+?)\s*```/g;

      let match;
      const actions: BotAction[] = [];
      let botInfo = chatResponse;

      while ((match = regex.exec(chatResponse)) !== null) {
        const jsonBlock = match[1]; 
        botInfo = botInfo.replace(match[0], ''); 
        try {
          const jsonContent = JSON.parse(jsonBlock);

          if (jsonContent.botNavigationLink) {
            const links = Array.isArray(jsonContent.botNavigationLink)
              ? jsonContent.botNavigationLink
              : [jsonContent.botNavigationLink];
            actions.push(
              ...links.map((link: string) => ({
                type: 'link',
                content: link,
              }))
            );
          }

          if (jsonContent.botAction) {
            actions.push({
              type: 'function',
              content: jsonContent.botAction,
            });
          }
        } catch (error) {
        }
      }


      const formattedMessage: ChatMessage = {
        text: botInfo.trim(),
        sender: 'bot',
        animationComplete: false,
        actions,
      };

      dispatch({ type: SEND_MESSAGE_SUCCESS });
      dispatch({ type: RECEIVE_MESSAGE, payload: formattedMessage });

    } else {
      handleApiError(response, dispatch)
    }
  } catch (error: any) {
    handleApiError(error, dispatch)
  }
};


export const fetchHelp = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_HELP_REQUEST });
  try {
    const response = await apis({
      method: "GET",
      url: "help/getAll"
    });
    if (response && response.status === 200) {
      dispatch({
        type: FETCH_HELP_SUCCESS,
        payload: response.data
      });

    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const fetchUserHelp = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_USER_HELP_REQUEST });
  const userType = getUserType();
  try {
    const response = await apis({
      method: "POST",
      url: "help/usertype/getList",
      data: { UserType: userType }
    });
    if (response && response.status === 200) {
      dispatch({
        type: FETCH_USER_HELP_SUCCESS,
        payload: response.data
      });

    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const fetchCategories = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_CATEGORIES_REQUEST });
  try {
    const response = await apis({
      method: "GET",
      url: "/support/categories"
    });

    if (response && response.status === 200 && Array.isArray(response.data.categories)) {
      const dataList: DropdownOption[] = response.data.categories.map((item: any) => {
        return {
          value: item.ID,
          label: item.DisplayName || '',
          active: item.active !== undefined ? item.active === true : undefined,
        };
      });
      dispatch({
        type: FETCH_CATEGORIES_SUCCESS,
        payload: dataList
      });
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const createTicket = (ticketData: any) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: CREATE_TICKET_REQUEST });
  try {
    const isFormData = ticketData instanceof FormData;

    const config: any = {
      method: 'POST',
      url: '/support/tickets',
    };

    if (isFormData) {
      config.data = ticketData;
    } else {
      config.data = {
        title: ticketData.title || ticketData.Subject || '',
        description: ticketData.description || ticketData.Description || '',
        priority: ticketData.priority || 'medium',
        organization_id: ticketData.organization_id,
      };
    }

    const response = await apis(config);

    if (response.data && response.data.success) {
      dispatch({
        type: CREATE_TICKET_SUCCESS,
        payload: response.data,
      });

      const attachmentMsg = response.data.attachments_uploaded
        ? ` (${response.data.attachments_uploaded} files attached)`
        : '';

      dispatch(showFeedbackModal({
        showModal: true,
        message: (response.data.message || 'Ticket created successfully') + attachmentMsg,
        modalType: 'success',
        duration: 3000,
      }));
      return response.data;
    } else {
      const errorMessage = response.data?.message || 'Failed to create ticket';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create ticket';
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
  }
};

export const fetchTicket = (ticketId: string) => async (dispatch: any) => {
  dispatch({ type: FETCH_TICKET_REQUEST });
  try {
    const response = await apis({
      method: 'GET',
      url: `/support/tickets/${ticketId}`,
    });

    if (response.data && response.data.success && response.data.ticket) {
      dispatch({
        type: FETCH_TICKET_SUCCESS,
        payload: response.data.ticket
      });
      return { payload: response.data.ticket };
    } else {
      const errorMessage = response.data?.message || 'Failed to fetch ticket';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch ticket';
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
  }
};

export const fetchAllUserTickets = (params: {
  status?: string;
  priority?: string;
  limit?: number;
  offset?: number;
  reset?: boolean;
} = {}) => async (dispatch: any, getState: () => RootState) => {
  dispatch({ type: FETCH_ALL_USER_TICKETS_REQUEST });
  try {
    const {
      status,
      priority,
      limit = 50,
      offset = 0,
      reset = false,
    } = params;

    const queryParams: any = {
      limit,
      offset,
    };

    if (status) queryParams.status = status;
    if (priority) queryParams.priority = priority;

    const response = await apis({
      method: 'GET',
      url: '/support/tickets',
      params: queryParams,
    });

    if (response.data && response.data.success && Array.isArray(response.data.tickets)) {
      const { tickets = [], total = 0 } = response.data;
      const currentTickets = reset || offset === 0 ? [] : getState().help.allUserTickets || [];
      const updatedTickets = offset === 0 ? tickets : [...currentTickets, ...tickets];
      const currentPage = Math.floor(offset / limit) + 1;
      const totalPages = Math.ceil(total / limit);

      dispatch({
        type: FETCH_ALL_USER_TICKETS_SUCCESS,
        payload: { tickets: updatedTickets, total, currentPage, totalPages },
      });
      return { tickets: updatedTickets, total, currentPage, totalPages };
    } else {
      const errorMessage = response.data?.message || 'Failed to fetch tickets';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch tickets';
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
    return null;
  }
};

export const fetchAllTickets = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_ALL_TICKETS_REQUEST });
  try {
    const response = await apis({
      method: "GET",
      url: "/support/get_all_tickets"
    });
    if (response && response.status === 200) {
      dispatch({
        type: FETCH_ALL_TICKETS_SUCCESS,
        payload: response.data
      });
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const fetchResolvedTickets = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_RESOLVED_TICKETS_REQUEST });
  try {
    const response = await apis({
      method: "GET",
      url: "/support/get_resolved_tickets"
    });
    if (response && response.status === 200) {
      dispatch({
        type: FETCH_RESOLVED_TICKETS_SUCCESS,
        payload: response.data
      });
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const fetchUnresolvedTickets = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_UNRESOLVED_TICKETS_REQUEST });
  try {
    const response = await apis({
      method: "GET",
      url: "/support/get_unresolved_tickets"
    });

    if (response && response.status === 200) {
      dispatch({
        type: FETCH_UNRESOLVED_TICKETS_SUCCESS,
        payload: response.data
      });

    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

export const resolveTicket = (ticketId: string) => async (dispatch: Dispatch) => {
  dispatch({ type: RESOLVE_TICKET_REQUEST });
  try {
    const response = await apis({
      method: "POST",
      url: "/support/resolve_ticket",
      data: { ticketId }
    });

    if (response && response.status === 200) {
      dispatch({
        type: RESOLVE_TICKET_SUCCESS,
        payload: response.data
      });
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
};

// ============= COMMENT ACTIONS =============

export const fetchTicketComments = (ticketId: string, limit = 50, offset = 0) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  try {
    const response = await apis({
      method: 'GET',
      url: `/support/tickets/${ticketId}/comments`,
      params: { limit, offset },
    });

    if (response.data && response.data.success) {
      return response.data;
    } else {
      const errorMessage = toErrorText(response.data?.error) || 'Failed to fetch comments';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    return null;
  }
};

export const createTicketComment = (ticketId: string, content: string, isInternal = false) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  try {
    const response = await apis({
      method: 'POST',
      url: `/support/tickets/${ticketId}/comments`,
      data: {
        content,
        is_internal: isInternal,
      },
    });

    if (response.data && response.data.success) {
      return response.data;
    } else {
      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Something went wrong while posting your comment.'),
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    dispatch(showFeedbackModal({
      showModal: true,
      message: translate('Something went wrong while posting your comment.'),
      modalType: 'error',
      duration: 3000,
    }));
    return null;
  }
};

export const updateTicketComment = (ticketId: string, commentId: number, content: string) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  try {
    const response = await apis({
      method: 'PATCH',
      url: `/support/tickets/${ticketId}/comments/${commentId}`,
      data: { content },
    });

    if (response.data && response.data.success) {
      return response.data;
    } else {
      const errorMessage = toErrorText(response.data?.error) || 'Failed to update comment';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to update comment');
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
    return null;
  }
};

export const deleteTicketComment = (ticketId: string, commentId: number) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  try {
    const response = await apis({
      method: 'DELETE',
      url: `/support/tickets/${ticketId}/comments/${commentId}`,
    });

    if (response.data && response.data.success) {
      return response.data;
    } else {
      const errorMessage = toErrorText(response.data?.error) || 'Failed to delete comment';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to delete comment');
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
    return null;
  }
};

export const updateTicketStatus = (ticketId: string, newStatus: string) => async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
  try {
    const response = await apis({
      method: 'PATCH',
      url: `/support/tickets/${ticketId}/status`,
      data: { status: newStatus },
    });

    if (response.data && response.data.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: response.data.message || 'Ticket status updated successfully',
        modalType: 'success',
        duration: 2000,
      }));
      return response.data;
    } else {
      const errorMessage = toErrorText(response.data?.error) || 'Failed to update status';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to update status');
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 3000,
    }));
    return null;
  }
};
